import { Injectable, LOCALE_ID, OnDestroy, computed, inject, signal, untracked } from '@angular/core';
import { Router } from '@angular/router';
import { ExternalHost } from '@app/models/external-host';
import { ScopeServer } from '@app/models/scope-server';
import { CommunityConsentService } from '@app/modules/scope-community/community-profile/services/community-consent.service';
import { CommunityProfileService } from '@app/modules/scope-community/community-profile/services/community-profile.service';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, InteractionStatus } from '@azure/msal-browser';
import { IdTokenClaim } from '@models/id-token-claims';
import { Constants } from '@shared/constants';
import { CookieService } from 'ngx-cookie-service';
import { Subject, catchError, filter, finalize, map, of, shareReplay, switchMap, take, takeUntil, zip } from 'rxjs';
import { environment } from '../../environments/environment';
import { loginRequest, tokenRequest } from '../msal.config';
import { LocalStorageService } from './local-storage.service';
import { RegistrarService } from './registrar.service';
import { UserProfileService } from './user-profile.service';

/** service to manage user authentication */
@Injectable( {
    providedIn: 'root'
} )
export class AuthService implements OnDestroy {
    /** flag, if loggedin */
    readonly isLoggedIn = signal( false );
    /** flag, if login should be disabled */
    readonly loginDisabled = signal( true );

    /**  lifecycle hook */
    readonly #destroyed: Subject<void> = new Subject<void>();

    locale = inject( LOCALE_ID );

    #msalService = inject( MsalService );
    #cookieService = inject( CookieService );
    #broadcastService = inject( MsalBroadcastService );
    #localStorageService = inject( LocalStorageService );
    #communityProfileService = inject( CommunityProfileService );
    #communityConsentService = inject( CommunityConsentService );
    #userProfileService = inject( UserProfileService );
    #registrarService = inject( RegistrarService );
    #router = inject( Router );

    readonly currentUser = signal<any>( {} );
    readonly userRoles = Constants.roles;

    /** computed role signals */
    readonly isAdmin = computed( () => this.currentUser()?.role === this.userRoles.Scope_Administrator );
    readonly isSuperAdmin = computed( () => this.currentUser()?.role === this.userRoles.RSI_Super_Administrator );
    readonly isScopeUser = computed( () => this.currentUser()?.role === this.userRoles.Scope_User );

    readonly isCommunityMember = computed<boolean>( () => this.currentUser()?.communityMember );
    readonly hasCommunityProfile = computed<boolean>( () => this.currentUser()?.hasProfile );

    readonly servers = signal<ScopeServer[]>( [] );
    readonly selectedServer = signal<ScopeServer | null>( null );

    readonly regions = computed<string[]>( () => {
        const servers = this.servers();
        const regions: string[] = [];

        untracked( () => {
            for ( const server of servers ) {
                if ( server.alternativeHostnames && server.alternativeHostnames.length ) {
                    server.alternativeHostnames.map( ( host: ExternalHost ) => {
                        // guard: if region is not already in list, add it
                        if ( !regions.includes( host.region ) ) {
                            regions.push( host.region );
                        }
                    } );
                }
            }
        } );
        return regions;
    } );

    readonly region = signal<string>( this.#cookieService.get( 'scope.region' ) || '' );

    /**
     * on init the app listens on msal broadcast service
     * which returns an event message of login state
     *
     * in case of success
     * - user data is stored in localStorage
     * - and loginState is set to trigger frontend updates
     * @param {string} locale code
     * @param localStorageService localstorage service
     */
    constructor() {
        /** initialize login state */
        this.#msalService.instance.initialize().then( () => {
            this.isLoggedIn.set( this.hasActiveAccount() );

            // restore account after reload when logged in
            if ( this.isLoggedIn() ) {
                const account = this.#localStorageService.load( 'account' );
                if ( account && account.organization ) {
                    this.currentUser.set( account );
                } else {
                    this.handleLogout();
                }
            }

            this.loginDisabled.set( false );
        } );
    }

    /**
     * login function
     * which redirects to MS Azure SSO Login
     * when successful, user will get redirected back to app
     */
    login() {
        this.loginDisabled.set( true );
        this.#broadcastService.inProgress$
            .pipe(
                filter( ( status: InteractionStatus ) => status === InteractionStatus.None ),
                takeUntil( this.#destroyed )
            )
            .subscribe( () => {
                this.#msalService.loginRedirect( loginRequest );
            } );
    }

    /**
     * logout function
     * redirects to login
     */
    logout() {
        this.loginDisabled.set( true );

        this.clearState();
        this.#msalService.logoutRedirect();
    }

    /**
     * helper function, get active account
     * @returns {boolean} flag, if account is active
     */
    hasActiveAccount() {
        return this.#msalService.instance.getActiveAccount() !== null;
    }

    /**
     * helper function, get Azure token
     * @param {string} tokenType type of token
     * @returns {string} token
     */
    async getTokenType( tokenType: any ): Promise<string> {
        const activeAccount = this.#msalService.instance.getActiveAccount();

        // force cache refresh and set refresh token lifetime
        // 2 hours * 60 minutes * 60 seconds = 7200 seconds
        const authTokenRequest = {
            ...tokenRequest,
            tokenType,
            activeAccount,
            refreshTokenExpirationOffsetSeconds: 7200
        };

        let token = '';
        await this.#msalService.instance
            .acquireTokenSilent( authTokenRequest )
            .then( ( response ) => {
                // save silently aquired token
                if ( response[ 'accessToken' ] || response[ 'accessToken' ] !== '' ) {
                    token = response[ 'accessToken' ] as string;
                }
                // or fallback to interaction when silent call fails
                else {
                    this.#msalService.instance.acquireTokenRedirect( authTokenRequest );
                }
            } )
            .catch( async ( error ) => {
                // brute force logout
                this.logout();
                throw error;
            } );

        return token;
    }

    /**
     * helper function to retrieve accessToken
     *
     * @returns {string} accessToken to validate access for current user
     */
    retrieveAccessToken() {
        return this.getTokenType( 'accessToken' );
    }

    /**
     * helper function to retrieve idToken
     *
     * NOTE: this function will be used once the tenant switch got sorted in backend
     * @returns {string} idToken to identify the tenant
     */
    retrieveIdentityToken() {
        return this.getTokenType( 'idToken' );
    }

    /**
     * switch scope tenant
     *
     * NOTE: this function will be used once the tenant switch got sorted in backend
     * @param {string} scopeTID id of scope tenant
     */
    async switchTenant( scopeTID: string ) {
        this.retrieveIdentityToken().then( async ( token: string | undefined ) => {
            console.log( 'IdentityToken', token );
            await this.#msalService.instance.clearCache();
            console.log( 'Cache cleared' );

            await this.#msalService.instance
                .ssoSilent( {
                    extraQueryParameters: {
                        id_token_hint: token || '',
                        scopeTenantID: scopeTID
                    },
                    ...tokenRequest
                } )
                .then( ( response ) => {
                    console.log( 'sso silent', response );
                    this.handleAuthResponse( response );
                } )
                .catch( async ( err ) => {
                    console.log( 'catched error', err );
                    await this.#msalService.instance
                        .loginRedirect( {
                            extraQueryParameters: {
                                id_token_hint: token || '',
                                scopeTenantID: scopeTID
                            },
                            ...tokenRequest
                        } )
                        .then( ( response ) => {
                            console.log( 'login redirect', response );
                            this.handleAuthResponse( response );
                        } );
                } );
        } );
    }

    /**
     * helper function, set account after login
     * @param {object} response login payload
     *
     * each api call in the zip is individually protected with catchError
     * so a single failing call does not abort the entire zip and leave
     * communityMember / hasProfile in an undefined state
     */
    async handleAuthResponse( response: any ) {
        this.#msalService.instance.setActiveAccount( response );

        const { name, homeAccountId } = response as AccountInfo;
        const {
            rsiRole: role,
            scopeTenantID: scopeTenantID,
            rsiOrganization: organizationOID,
            organizationName = 'Scope'
        } = response?.idTokenClaims as unknown as IdTokenClaim;

        const OID = homeAccountId.indexOf( '-b2c_1a' )
            ? homeAccountId.substring( 0, homeAccountId.indexOf( '-b2c_1a' ) )
            : homeAccountId;

        const organization$ = this.#registrarService.getScopeTenantByID( scopeTenantID )
            .pipe(
                shareReplay( { bufferSize: 1, refCount: true } ),
                // guard: if organization call fails, continue with null so zip does not abort
                catchError( () => of( null ) )
            );

        const hasProfile$ = organization$.pipe(
            take( 1 ),
            switchMap( org => {
                // communityId resolved once and reused — organizationOID (UUID) preferred, scopeCode as legacy fallback
                const communityId = organizationOID ?? org?.scopeCode;
                return this.#communityProfileService.getCommunityProfile( communityId );
            } ),
            // guard: if profile call fails, treat as no profile instead of aborting zip
            catchError( () => of( null ) )
        );

        const consent$ = organization$.pipe(
            take( 1 ),
            switchMap( org => {
                // reuse same communityId resolution as hasProfile$
                const communityId = organizationOID ?? org?.scopeCode;
                return this.#communityConsentService.getCommunityConsent( communityId );
            } ),
            // guard: already present — if consent call fails, treat as no consent
            catchError( () => of( false ) )
        );

        const user$ = this.#userProfileService.getUserProfile( OID ).pipe(
            // guard: if user profile call fails, continue with safe defaults so zip does not abort
            // all fields used in the account object below must be present to avoid type errors
            catchError( () => of( {
                avatarURL: 'avatar_placeholder',
                email: '',
                alternativeEmails: [],
                phone: '',
                formatLocale: this.locale,
                preferredLanguage: navigator.language
            } ) )
        );

        const servers$ = this.#registrarService.getScopeServer( scopeTenantID )
            .pipe( take( 1 ),
                map( ( scopeServers: ScopeServer[] ) => scopeServers ),
                // guard: if server call fails, continue with empty array so zip does not abort
                catchError( () => of( [] ) )
            );

        // NOTE: tenant$ will be used once the tenant switch got sorted in backend
        // const tenant$ = this.#identityService.getScopeTenants(OID);

        // merge api call responses into user object
        let isLoggedIn = false;
        zip( organization$, hasProfile$, consent$, user$, servers$ )
            .pipe(
                map( ( [ organization, hasProfile, communityMember, user, servers ] ) => ( { organization, hasProfile, communityMember, user, servers } ) ),
                finalize( () => this.isLoggedIn.set( isLoggedIn ) )
            )
            .subscribe( ( results ) => {
                // communityId resolved once — organizationOID (UUID) preferred, scopeCode as legacy fallback
                const communityId = organizationOID ?? results.organization?.scopeCode;
                const storedAccount = this.#localStorageService.load( 'account' );

                // NOTE: the account object is stored in localStorage and used to restore state after reloads
                const account = {
                    OID,
                    role,
                    name,
                    // communityId — used for profile navigation
                    // organizationOID is the UUID that the community backend uses
                    // fallback to scopeCode for legacy compatibility
                    communityId,
                    hasProfile: results.hasProfile ? true : false,
                    communityMember: results.communityMember,
                    avatarURL: results.user.avatarURL ?? 'avatar_placeholder',
                    email: results.user.email,
                    alternativeEmails: results.user.alternativeEmails,
                    phone: results.user.phone,
                    organizationName,
                    organization: results.organization,
                    organizationOID,
                    scopeTenantID,
                    // tenants: results.tenants,
                    // selectedTenant: selectedTenant || results.tenants[0],
                    formatLocale: storedAccount?.formatLocale ?? results.user.formatLocale ?? this.locale,
                    preferredLanguage: storedAccount?.preferredLanguage ?? results.user.preferredLanguage ?? navigator.language
                };

                /** set account */
                this.currentUser.set( account );
                this.#localStorageService.save( 'account', account );

                isLoggedIn = true;

                // set assigned servers to signal and select first server as default
                if ( results.servers.length ) {
                    this.servers.set( results.servers );
                    this.selectedServer.set( results.servers[ 0 ] );

                    // when alternate hosts are available select alternate host based on region setting
                    if ( this.regions().length ) {
                        // guard: if region not set, determine default based on user timezone
                        // avoids relying on backend array order which may differ per environment
                        if ( !this.region() ) {
                            this.region.set( this.#getDefaultRegion() );

                            // As of Chrome release M104 (August 2022) cookies can no longer
                            // set an expiration date more than 400 days in the future.
                            const expireDate = new Date();
                            expireDate.setDate( expireDate.getDate() + 400 );
                            this.#cookieService.set( 'scope.region', this.region(), expireDate, '/' );
                        }
                        // edge case - fall back to default region
                        else if ( !this.regions().includes( this.region() ) ) {
                            this.region.set( this.#getDefaultRegion() );
                        }

                        const selectedServer = this.selectedServer();
                        // check if the selected region has a matching host and overwrite the first selected server with that host
                        if ( selectedServer!.alternativeHostnames && selectedServer!.alternativeHostnames.length ) {
                            const regionalServer = selectedServer!.alternativeHostnames
                                .filter( ( host: ExternalHost ) => host.region === this.region() )[ 0 ];

                            if ( regionalServer !== undefined ) {
                                const selectedRegionalServer = Object.assign( { ...selectedServer!, ...regionalServer } ) as ScopeServer;
                                this.selectedServer.set( selectedRegionalServer );
                            }
                        }
                    }
                }
                else {
                    console.warn( 'There is no scope server configured' );
                }

                /**
                 * handle case where language differs in user profile
                 * only de is supported natively — all other languages fall back to en
                 *
                 * cookie and reload are only triggered when the app language
                 * doesn't already match the supported language —
                 * this prevents handleAuthResponse from overwriting a manual
                 * language change that just triggered a reload
                 */
                const language = account.preferredLanguage.slice( 0, 2 );
                const supportedLanguage = language === 'de' ? 'de' : 'en';
                const currentDocLang = document.documentElement.lang;
                const currentCookieLang = this.#cookieService.get( 'language' );;

                if ( supportedLanguage !== currentDocLang ) {
                    this.#cookieService.set( 'language', supportedLanguage, undefined, '/' );
                    if ( environment.name !== 'local' ) {
                        window.setTimeout( () => this.windowReload(), 200 );
                    }
                }
            } );
    };

    /**
     * determine default region based on user's timezone
     * falls back to EUROPE if timezone is not Asia
     * fixes issue where backend returns regions in alphabetical order (ASIA before EUROPE)
     * which caused new users without a region cookie to be assigned to the wrong server
     * @returns {string} region as string
     */
    #getDefaultRegion(): string {
        const available = this.regions();
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const byTimezone = tz.startsWith( 'Asia' ) ? 'ASIA' : 'EUROPE';

        return available.includes( byTimezone )
            ? byTimezone
            : available.includes( 'EUROPE' )
                ? 'EUROPE'
                : available[ 0 ] ?? 'EUROPE';
    }

    /** unset states to clean up app in browser after logout */
    clearState() {
        this.currentUser.set( {} );
        this.isLoggedIn.set( false );

        this.#localStorageService.clear();
        this.#cookieService.delete( 'language' );
    }

    handleLogin() {
        this.#cookieService.delete( 'login' );
        this.#msalService.loginRedirect( loginRequest );
    }

    /**
     * helper function
     * trigger app cleanup and navigate to home screen
     */
    handleLogout() {
        this.clearState();

        /** redirect to home page */
        this.#router.navigateByUrl( '/', {
            replaceUrl: true
        } );
    }

    /**
     * helper function to update account information
     * with changes in user profile
     * @param {object} settings properties to be updated
     */
    updateAccount( settings: any ) {
        const account = this.#localStorageService.load( 'account' );
        const updatedAccount = { ...account, ...settings };

        this.#localStorageService.save( 'account', updatedAccount );
        this.currentUser.set( updatedAccount );
    }

    /** helper function to reload window */
    windowReload() {
        window.location.reload();
    }

    /**
     * handle app destroy
     * and unsubscribe from detroy handler
     */
    ngOnDestroy(): void {
        this.#destroyed.next( undefined );
        this.#destroyed.complete();
    }
}
